/* @flow */
import React, { useContext, useEffect, useState } from 'react';
import uniqBy from 'lodash/uniqBy';
import { useDispatch, useSelector, useStore } from 'react-redux';
import Breadcrumbs from '../../components/Breadcrumbs';
import TilesObjects from '../../components/TilesObjects';
import Preloader from '../../components/Preloader';

import { Root, Header, SearchWrapper, SearchPanel, Section, Title, List, ButtonContainer } from './styles';
import { Container } from '../../components/Layout/styles';

import Button from '../../components/Button2.0';
import { AppContext } from '../../components/AppContext';
import { generateBreadcrumbs } from '../../utils/breadcrumbs';
import { getBuyerArticlesData } from '../../actions/buyerList';
import { checkArticlesCity } from '../../utils/misc';
import type { TCity } from '../../types/city';
import type { TArticles } from '../../types/articles';
import type { TDispatch } from '../../types/core';

const searchData = {
	type: 'text',
	placeholder: 'Поиск по разделу',
};

const BuyerList = () => {
	const dispatch: TDispatch = useDispatch();
	const store = useStore();
	const context = useContext(AppContext);
	const city: TCity = useSelector((state) => state.city);
	const buyerList: TArticles = useSelector((state) => state.buyerList);
	const [search, setSearch] = useState('');
	const [currentPage, setCurrentPage] = useState(2);
	const [isLast, setIsLast] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [articles, setArticles] = useState(buyerList.articlesData.items);

	const COUNT_PER_PAGE = buyerList?.articlesData?.meta?.itemsOnPage || 20;

	useEffect(() => {
		const correctArticles = checkArticlesCity(buyerList.articlesData.items, city);

		if (!buyerList.loading) {
			if (!buyerList.articlesData.items || correctArticles === false) {
				getBuyerArticlesData()(dispatch, store.getState, context);
			}
		}
	}, [city, buyerList]);

	const getBody = (value, page) => {
		const body = {
			type: 'mystery_buyer',
			searchString: value,
		};
		if (page) {
			body.page = page;
		}
		return body;
	};

	const handleChangeSearch = async (value) => {
		const searchURL = '/lexsApi/search/content';

		setSearch(value);
		setCurrentPage(2);

		if (!value.length) {
			setArticles(articles);
		}

		if (value.length > 1) {
			setIsLoading(true);

			const res = await context.fetch(searchURL, {
				method: 'POST',
				body: JSON.stringify({ ...getBody(value), cityIds: city.cityIds }),
			});
			const { data } = await res.json();

			setIsLoading(false);
			setArticles(data.items);
		}
	};

	const loadMore = async () => {
		let URL = `/lexsApi/content/mystery-buyer/list`;
		let res;

		setIsLoading(true);

		if (search) {
			URL = '/lexsApi/search/content';
			res = await context.fetch(URL, {
				method: 'POST',
				body: JSON.stringify({
					...getBody(search, currentPage - 1),
					cityIds: city.cityIds,
				}),
			});

			setIsLoading(true);

			const { data } = await res.json();

			setIsLoading(false);

			setArticles(uniqBy([...articles, ...data.items], 'id'));
			setCurrentPage(currentPage + 1);

			if (articles.length >= data.total - 1) {
				setIsLast(true);
			}
		} else {
			res = await context.fetch(URL, {
				method: 'POST',
				body: JSON.stringify({
					cityIds: city.cityIds,
					page: currentPage - 1,
					count: COUNT_PER_PAGE,
				}),
			});
			const { data } = await res.json();

			setIsLoading(false);

			if (data.meta.pageCount <= currentPage) {
				setIsLast(true);
			}

			setArticles(uniqBy([...articles, ...data.items], 'id'));
			setCurrentPage(currentPage + 1);
		}
	};

	const countPerPage = search ? 30 : COUNT_PER_PAGE;

	const breadcrumbsArray = [
		{
			index: 3,
			title: 'Экспертиза',
			link: `/${city.link}/expert/`,
		},
		{
			index: 4,
			title: 'Тайный покупатель',
			link: `/tainyi-pokupatel/`,
		},
	];

	const breadcrumbs = generateBreadcrumbs(city.link, breadcrumbsArray);

	return (
		<Root>
			<Container>
				<Breadcrumbs data={breadcrumbs} />

				<Header>
					<Title>Тайный покупатель</Title>
					<SearchWrapper>
						<SearchPanel data={searchData} value={search} handleChangeSearch={handleChangeSearch} />
					</SearchWrapper>
				</Header>

				<Section>
					{articles.length > 0 && (
						<List>
							<TilesObjects data={articles} label="Тайный покупатель" />
						</List>
					)}
				</Section>
			</Container>

			{isLoading && <Preloader />}
			{!isLast && articles.length >= countPerPage && (
				<ButtonContainer>
					<Button onClick={loadMore} size="s" color="gray" style={{ padding: '0 30px' }}>
						Показать ещё
					</Button>
				</ButtonContainer>
			)}
		</Root>
	);
};

export default BuyerList;
